import { FC, useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Grid,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Avatar,
  Card,
  CircularProgress,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import FolderSpecialOutlinedIcon from "@mui/icons-material/FolderSpecialOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CheckIcon from "@mui/icons-material/Check";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import AddModeratorOutlinedIcon from "@mui/icons-material/AddModeratorOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { StripeSetupElement } from "src/sections/dashboard/stripe/stripe-setup-element";
import { useAuth } from "src/hooks/use-auth";

interface PlatformTrialDialogProps {
  open: boolean;
  onClose: () => void;
  onStartTrial?: () => void;
  imageUrl?: string; // URL for the right panel image
  priceId?: string; // Optional price ID for the subscription
  hohUdId?: string | null; // Head of household's user datum ID
  membershipPrice?: number; // Membership price in cents
  // Stripe-related props (passed from parent context)
  createSetupIntent: (ud_id: string) => Promise<{ client_secret: string; stripe_connect_id?: string } | null>;
  createTrialSubscription: (ud_id: string, payment_method_id: string, price_id?: string) => Promise<any>;
}

export const PlatformTrialDialog: FC<PlatformTrialDialogProps> = ({
  open,
  onClose,
  onStartTrial,
  imageUrl = "https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/premium_membership_hero.png", // Default image path
  priceId,
  hohUdId,
  membershipPrice,
  createSetupIntent,
  createTrialSubscription,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [currentView, setCurrentView] = useState<"initial" | "payment" | "test">(
    "initial"
  );
  const [paymentSuccess, setPaymentSuccess] = useState<boolean>(false);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<string | null>(null);
  const [stripeConnectAccountId, setStripeConnectAccountId] = useState<string | null>(null);
  const [setupIntentLoading, setSetupIntentLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const ud_id = hohUdId || user?.ud_id || null;

  // Calculate dates dynamically
  const today = new Date();
  const reminderDate = new Date(today);
  reminderDate.setDate(reminderDate.getDate() + 27); // 3 days before trial ends
  const trialEndDate = new Date(today);
  trialEndDate.setDate(trialEndDate.getDate() + 30); // 30 days from today

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
  };

  // Reset state when dialog is closed
  useEffect(() => {
    if (!open) {
      setPaymentSuccess(false);
      setCurrentView("initial");
      setSetupIntentClientSecret(null);
    }
  }, [open]);

  const handleStartTrial = () => {
    setCurrentView("payment");
  };

  const handleBack = () => {
    if (currentView === "payment" || currentView === "test") {
      setCurrentView("initial");
      setSetupIntentClientSecret(null); // Clear setup intent when going back
    } else {
      onClose();
    }
  };

  // Create SetupIntent when payment or test view is shown
  useEffect(() => {
    const loadSetupIntent = async () => {
      if ((currentView === "payment" || currentView === "test") && ud_id && !setupIntentClientSecret && !setupIntentLoading) {
        setSetupIntentLoading(true);
        try {
          const result = await createSetupIntent(ud_id);
          if (result?.client_secret) {
            setSetupIntentClientSecret(result.client_secret);
            // Use the Connect account ID returned from backend
            setStripeConnectAccountId(result.stripe_connect_id || null);
          }
        } catch (error) {
          console.error("Error creating setup intent:", error);
        } finally {
          setSetupIntentLoading(false);
        }
      }
    };

    loadSetupIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, ud_id]);

  const handleSetupSuccess = useCallback(
    async (paymentMethodId: string) => {
      setPaymentSuccess(true);

      // Create trial subscription with payment method
      if (ud_id && paymentMethodId && priceId) {
        try {
          await createTrialSubscription(ud_id, paymentMethodId, priceId);
        } catch (error) {
          console.error("Error creating trial subscription:", error);
          // Continue anyway - user can still access trial features via webhook
        }
      }

      if (onStartTrial) {
        onStartTrial();
      }

      // Close dialog after a short delay to show success
      setTimeout(() => {
        onClose();
      }, 2000);
    },
    [onStartTrial, onClose, ud_id, priceId, createTrialSubscription]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          // maxWidth: '900px',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ minHeight: isMobile ? "auto" : "500px" }}>
          {/* Left Panel - Content */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            {/* Main content */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {/* Title with back chevron */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <IconButton
                  onClick={handleBack}
                  sx={{
                    ml: -1,
                    color: "text.primary",
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="h4">
                  Try Premium for free
                </Typography>
              </Stack>

              {currentView === "initial" ? (
                <>
                  {/* Initial view content */}

                  <Typography variant="body1" sx={{ fontWeight: 400, mb: 3 }}>
                    More protection. More legacy. Unlock lasting benefits for
                    your whole family. Try our most-loved features, free for 30
                    days.
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    Here's what you get with Premium membership.
                  </Typography>

                  {/* Features list */}
                  <Stack spacing={2} sx={{ mb: 4 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <SvgIcon
                        sx={{
                          fontSize: 32,
                          color: "text.primary",
                          mt: -0.5,
                        }}
                      >
                        <FolderSpecialOutlinedIcon />
                      </SvgIcon>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "text.primary",
                          }}
                        >
                          Stay covered with <b>free Will and Trust updates</b>{" "}
                          and the comprehensive <b>Total Planning Vault.</b>
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <SvgIcon
                        sx={{
                          fontSize: 32,
                          color: "text.primary",
                          mt: -0.5,
                        }}
                      >
                        <GroupsOutlinedIcon />
                      </SvgIcon>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "text.primary",
                          }}
                        >
                          Access when you need it, with <b>Family Sharing</b>{" "}
                          and a <b>Wallet Card</b> for instant access anytime.
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <SvgIcon
                        sx={{
                          fontSize: 32,
                          color: "text.primary",
                          mt: -0.5,
                        }}
                      >
                        <VideocamOutlinedIcon />
                      </SvgIcon>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "text.primary",
                          }}
                        >
                          Record and store <b>Video Messages</b> to share
                          stories and words of wisdom.
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>

                  {/* Bottom section with buttons */}
                  <Box sx={{ mt: "auto" }}>
                    <Typography variant="body1" sx={{ fontWeight: 400, mb: 3 }}>
                      <b>Cancel any time. </b>We'll remind you 3 days before
                      your trial ends.
                    </Typography>

                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      onClick={handleStartTrial}
                      sx={{
                        py: 1.5,
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 500,
                      }}
                    >
                      Start my free trial
                    </Button>
                  </Box>
                </>
              ) : currentView === "payment" ? (
                <>
                  {/* Payment view content */}
                  <Stack spacing={2} sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckIcon sx={{ fontSize: 20, color: "success.main" }} />
                      <Typography variant="body2">
                        Free 30 day trial, cancel any time
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckIcon sx={{ fontSize: 20, color: "success.main" }} />
                      <Typography variant="body2">
                        We'll remind you before your trial ends
                      </Typography>
                    </Stack>
                  </Stack>

                  {/* Pricing summary */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ mb: 1 }}
                    >
                      Order Summary
                    </Typography>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Premium Yearly</Typography>
                        <Typography variant="body2">
                          ${((membershipPrice || 11999) / 100).toFixed(2)}/year
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Due today (30 days free)
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "success.main" }}
                        >
                          $0
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Stripe Payment Element */}
                  <Box sx={{ mb: 3 }}>
                    {setupIntentLoading ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 4,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : paymentSuccess ? (
                      <Card
                        variant="outlined"
                        sx={{ p: 3, bgcolor: "#f0f9ff", textAlign: "center" }}
                      >
                        <CheckIcon
                          sx={{ fontSize: 48, color: "success.main", mb: 2 }}
                        />
                        <Typography variant="h6" color="success.main">
                          Payment method saved!
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Your free trial is being activated...
                        </Typography>
                      </Card>
                    ) : (
                      <StripeSetupElement
                        setupIntentClientSecret={setupIntentClientSecret}
                        stripeConnectAccountId={stripeConnectAccountId}
                        handleSetupSuccess={handleSetupSuccess}
                        onCancel={() => setCurrentView("initial")}
                      />
                    )}
                  </Box>
                  <StripeSetupElement
                        setupIntentClientSecret={setupIntentClientSecret}
                        stripeConnectAccountId={stripeConnectAccountId}
                        handleSetupSuccess={handleSetupSuccess}
                        onCancel={() => setCurrentView("initial")}
                      />

                  {/* Terms and Privacy */}
                  {!paymentSuccess && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: "auto", lineHeight: "inherit" }}
                      >
                        By continuing, you agree to the{" "}
                        <Box
                          component="a"
                    href="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/User+Terms+of+Service.pdf"
                    target="_blank"
                          sx={{
                            textDecoration: "underline",
                            cursor: "pointer",
                            color:'inherit'
                          }}
                        >
                          
                          Terms of Use
                        </Box>{" "}
                        applicable to Premium. Your payment info is encrypted and secure. You won't
                        be charged until after your trial ends.
                      </Typography>
                    </Box>
                  )}
                </>
              ) : null}
            </Box>
          </Grid>

          {/* Right Panel */}
          {!isMobile && (
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                background:
                  currentView === "initial"
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "#f8f9fa",
                display: "flex",
                alignItems: currentView === "initial" ? "center" : "flex-start",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                p: currentView === "payment" ? 4 : 0,
              }}
            >
              {currentView === "initial" ? (
                <>
                  {/* Initial view - Image panel */}
                  {/* Maybe Later button in top-right of image panel */}
                  <Button
                    onClick={onClose}
                    variant="outlined"
                    color="inherit"
                    sx={{
                      position: "absolute",
                      right: 16,
                      top: 16,
                      zIndex: 2,
                    }}
                  >
                    Maybe Later
                  </Button>

                  {/* Decorative circles */}
                  <Box
                    sx={{
                      position: "absolute",
                      width: "300px",
                      height: "300px",
                      borderRadius: "50%",
                      background: "rgba(255, 255, 255, 0.1)",
                      top: "-100px",
                      right: "-100px",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      width: "200px",
                      height: "200px",
                      borderRadius: "50%",
                      background: "rgba(255, 200, 87, 0.3)",
                      bottom: "-50px",
                      left: "-50px",
                    }}
                  />

                  {/* Image container */}
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      width: "100%",
                      "& img": {
                        width: "100%",
                        height: "auto",
                        display: "block",
                      },
                    }}
                  >
                    {imageUrl && (
                      <img
                        src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/premium_membership_hero.png"
                        alt="Snug Pro Features"
                        style={{
                          width: "100%",
                          height: "auto",
                        }}
                      />
                    )}
                  </Box>
                </>
              ) : (
                <>
                  {/* Pricing/Payment view - Timeline and testimonial */}
                  <Stack
                    spacing={4}
                    justifyContent={"space-between"}
                    sx={{ width: "100%", height: "100%" }}
                  >
                    {/* Maybe Later button */}
                    {/* <Button
                    onClick={onClose}
                    variant="outlined"
                    color='inherit'
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: 16,
                      zIndex: 2,
                    }}
                  >
                    Maybe Later
                  </Button> */}

                    {/* Timeline */}
                    <Card variant="outlined" sx={{ p: 5 }}>
                      <Box sx={{ position: "relative" }}>
                        {/* Connecting line */}
                        <Box
                          sx={{
                            position: "absolute",
                            left: 19,
                            top: 40,
                            width: 2,
                            height: "calc(100% - 40px)",
                            background: "#e0e0e0",
                            zIndex: 0,
                          }}
                        />
                        {/* Dashed extension below last icon */}
                        <Box
                          sx={{
                            position: "absolute",
                            left: 19,
                            bottom: -30,
                            width: 2,
                            height: 30,
                            borderLeft: "3px dashed #e0e0e0",
                            zIndex: 0,
                          }}
                        />

                        <Stack
                          spacing={6}
                          sx={{ position: "relative", zIndex: 1 }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="flex-start"
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                bgcolor: "#3e8a28",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                position: "relative",
                                zIndex: 2,
                              }}
                            >
                              <AddModeratorOutlinedIcon sx={{ fontSize: 24 }} />
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                Today - Free trial for 30 days. Cancel anytime.
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Start your free Premium trial and get our
                                most-loved features.
                              </Typography>
                            </Box>
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="flex-start"
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                bgcolor: "#e0e0e0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                zIndex: 2,
                              }}
                            >
                              <EventOutlinedIcon
                                sx={{ fontSize: 24, color: "#666" }}
                              />
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {formatDate(reminderDate)}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                We'll send you a reminder 3 days before your
                                Premium trial ends
                              </Typography>
                            </Box>
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="flex-start"
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                bgcolor: "#e0e0e0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                zIndex: 2,
                              }}
                            >
                              <VerifiedUserOutlinedIcon
                                sx={{ fontSize: 24, color: "#666" }}
                              />
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {formatDate(trialEndDate)}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Your subscription starts, unless you've canceled
                                during the trial.
                              </Typography>
                            </Box>
                          </Stack>
                        </Stack>
                      </Box>
                    </Card>

                    {/* Testimonial */}
                    <Box sx={{ mt: 4, p: 4 }}>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="flex-start"
                      >
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                          }}
                          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/mynor_v1.png"
                        />
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Mynor Ramos
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Planning Consultant
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{ mt: 2, fontStyle: "italic" }}
                      >
                        "Many people have bought into the myth that estate and legacy planning is only for the ultra wealthy - this couldn't be further from the truth. Everyone needs a plan and Premium makes it simple to stay protected."
                      </Typography>
                    </Box>
                  </Stack>
                </>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
